import React, { useState, useContext, useRef, useEffect } from "react";
import axios from "axios";
import { OperaContext } from "../../context/OperaContext";
import ImageModal from "../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../components/useGoogleDriveImagePicker";
import ImagePreview from "../../components/imagePreview";
import { toast } from "react-toastify";
import ImageCropModal from "./Event/_components/image-crop-modal";
import RichTextEditor from "../../components/RichTextEditor";
import { fa0 } from "@fortawesome/free-solid-svg-icons";

function ComposerTab() {
  const {
    allComposers = [],
    setAllComposers = () => { },
    fetchData,
  } = useContext(OperaContext);
  const [newComposer, setNewComposer] = useState({
    name: "",
    description: "",
    image: null,
  });
  const [editingComposer, setEditingComposer] = useState(null);
  const [hiddenComposers, setHiddenComposers] = useState([]);
  const [showHiddenComposers, setShowHiddenComposers] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const composerFormRef = useRef(null);
  const formContainerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [cropField, setCropField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  // Sort and filter composers based on search term
  const sortedVisibleComposers = [...allComposers]
    ?.sort((a, b) => a.name?.localeCompare(b.name))
    .filter((composer) =>
      composer.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const sortedHiddenComposers = [...hiddenComposers]
    ?.sort((a, b) => a.name?.localeCompare(b.name))
    .filter((composer) =>
      composer.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleComposerChange = (e) => {
    const { name, value } = e.target;
    setNewComposer((prev) => ({ ...prev, [name]: value }));
    if (editingComposer)
      setEditingComposer((prev) => ({ ...prev, [name]: value }));
  };

  const handleComposerFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file (e.g., JPG, PNG)");
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      e.target.value = null;
      return;
    }

    setError(null);

    if (name === "coverImage") {
      setOriginalFile(file);
      setCropField(name);

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setIsCropOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      setNewComposer((prev) => ({ ...prev, [name]: file }));
      if (editingComposer)
        setEditingComposer((prev) => ({ ...prev, [name]: file }));
    }
  };
  const handleDescriptionChange = (field, value) => {
    setNewComposer((prev) => ({ ...prev, [field]: value }));
    if (editingComposer) {
      setEditingComposer((prev) => ({ ...prev, [field]: value }));
    }
  };
  const fetchHiddenComposers = async () => {
    try {
      const res = await axios.get("/api/composers/hidden", { timeout: 15000 });
      setHiddenComposers(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching hidden composers:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
    }
  };

  const resetForm = () => {
    setShowPreview(false);
    setNewComposer({
      name: "",
      description: "",
      image: null,
    });
    if (composerFormRef.current) {
      composerFormRef.current.reset();
    }
  };

  const submitComposer = async (formData, isEdit = false, id = null) => {
    setShowPreview(false);
    try {
      const url = isEdit ? `/api/composers/${id}` : "/api/composers";
      const method = isEdit ? "put" : "post";
      const res = await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.status === 200 || res.status === 201) {
        toast.success(`${isEdit ? "Updated" : "Added"} composer Successfully`);
      }
      return res.data;
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "add"} composer`;
      toast.error(errorMsg);
    }
  };

  const handleComposerSubmit = async (e, retryCount = 0, maxRetries = 3) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", newComposer.name);
    formData.append("description", newComposer.description || ""); // Ensure description is never undefined
    if (editingComposer) {
      formData.append("image", editingComposer.image);
      formData.append("coverImage", editingComposer.coverImage);

    }
    if (!editingComposer) {
      formData.append("image", newComposer.image);
      formData.append("coverImage", editingComposer.coverImage);
    }

    try {
      setError(null);
      const result = await submitComposer(
        formData,
        !!editingComposer,
        editingComposer?._id
      );

      // Update local state immediately for better UX
      if (editingComposer) {
        setAllComposers((prev) =>
          prev.map((c) => (c._id === editingComposer._id ? result : c))
        );
      } else {
        setAllComposers((prev) => [...prev, result]);
      }

      setEditingComposer(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error("Error submitting composer:", {
        message: err.message,
        attempt: retryCount + 1,
      });
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        console.log(
          `Retrying composer submit in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1
          })`
        );
        setTimeout(
          () => handleComposerSubmit(e, retryCount + 1, maxRetries),
          delay
        );
      } else {
        setError(err.message);
      }
    }
  };

  const cancelEditing = () => {
    setEditingComposer(null);
    resetForm();
  };

  const editComposer = (composer) => {
    setEditingComposer(composer);
    setNewComposer({
      name: composer.name,
      description: composer.description || "",
      image: composer.image,
    });
    setSearchTerm("");
  };

  const deleteComposer = async (
    composerId,
    isHidden = false,
    retryCount = 0,
    maxRetries = 3
  ) => {
    if (!window.confirm("Are you sure you want to delete this composer?"))
      return;
    try {
      await axios.delete(`/api/composers/${composerId}`, { timeout: 15000 });
      console.log("Deleted composer:", composerId);
      if (isHidden) {
        setHiddenComposers((prev) => prev.filter((c) => c._id !== composerId));
      } else {
        setAllComposers((prev) => prev.filter((c) => c._id !== composerId));
      }
      setEditingComposer(null);
      resetForm();
      fetchData();
      setError(null);
    } catch (err) {
      console.error("Error deleting composer:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        attempt: retryCount + 1,
      });
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        console.log(
          `Retrying delete composer in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1
          })`
        );
        setTimeout(
          () =>
            deleteComposer(composerId, isHidden, retryCount + 1, maxRetries),
          delay
        );
      } else {
        setError(err.response?.data?.message || "Failed to delete composer");
      }
    }
  };

  const hideComposer = async (composerId) => {
    try {
      const res = await axios.put(
        `/api/composers/${composerId}/hide`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully hidden composer!");
      }
      fetchHiddenComposers();
      setAllComposers((prev) => prev.filter((c) => c._id !== composerId));
      fetchData();
    } catch (err) {
      toast.error("Error hiding composer:", err.message);
    }
  };

  const unhideComposer = async (composerId) => {
    try {
      const res = await axios.put(
        `/api/composers/${composerId}/unhide`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully unhidden composer!");
      }
      setHiddenComposers((prev) => prev.filter((c) => c._id !== composerId));
      setAllComposers((prev) => [...prev, res.data]);
      fetchData();
      setError(null);
    } catch (err) {
      toast.error("Error unhiding composer:", err.message);
    }
  };

  const publishComposer = async (composerId) => {
    try {
      const res = await axios.put(
        `/api/composers/${composerId}/publish`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully published composer!");
      }
      setAllComposers((prev) =>
        prev.map((c) => (c._id === composerId ? { ...c, published: true } : c))
      );
    } catch (err) {
      toast.error("Error publishing composer:", err.message);
    }
  };

  const unpublishComposer = async (composerId) => {
    try {
      const res = await axios.put(
        `/api/composers/${composerId}/unpublish`,
        {},
        { timeout: 15000 }
      );
      if (res.status === 200) {
        toast.success("Successfully unpublished composer!");
      }
      setAllComposers((prev) =>
        prev.map((c) => (c._id === composerId ? { ...c, published: false } : c))
      );
    } catch (err) {
      toast.success("Error unpublishing composer:", err.message);
    }
  };

  useEffect(() => {
    if (showHiddenComposers) fetchHiddenComposers();
    else setHiddenComposers([]);
  }, [showHiddenComposers]);

  // Scroll to form when editing a composer
  useEffect(() => {
    if (editingComposer && formContainerRef.current) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        const yOffset = -70; // Add extra offset to ensure form is fully visible
        const y =
          formContainerRef.current.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }, 100);
    }
  }, [editingComposer]);
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: editingComposer ? true : false,
    setFormData: setNewComposer,
    setFormDataEdit: setEditingComposer,
    setError,
  });
  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white flex flex-col gap-4">
      <ImageCropModal
        imageSrc={imageSrc}
        originalFileName={originalFile?.name || cropField || "Composer"}
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        onSave={(croppedFile) => {
          setIsCropOpen(false);

          setNewComposer((prev) => ({ ...prev, [cropField]: croppedFile }));
          if (editingComposer) {
            setEditingComposer((prev) => ({ ...prev, [cropField]: croppedFile }));
          }
        }}
      />
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <div ref={formContainerRef} id="composer-form-container">
        <h2 className="text-xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
          {editingComposer ? "Edit Composer" : "Add New Composer"}
        </h2>
        {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
        <form
          onSubmit={handleComposerSubmit}
          ref={composerFormRef}
          className="flex flex-col gap-4 mb-8"
          id="composer-form"
        >
          <input
            type="text"
            name="name"
            value={newComposer.name}
            onChange={handleComposerChange}
            placeholder="Composer Name"
            className="p-2 bg-[#333] border border-[#555] rounded text-white text-base"
            required
          />
          <button
            type="button"
            className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors disabled:bg-[#e6c347] disabled:cursor-not-allowed"
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview ? "Edit Text" : "Edit HTML"}
          </button>
          {showPreview && <textarea
            name="description"
            value={newComposer.description}
            onChange={handleComposerChange}
            placeholder="Composer Description"
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[150px] resize-y"
          />}
          {!showPreview && <RichTextEditor
            value={newComposer.description}
            height={200}
            onChange={(content) =>
              handleDescriptionChange("description", content)
            }
          />}
          {(editingComposer?.image || newComposer.image) && (
            <ImagePreview
              image={editingComposer?.image || newComposer.image}
              onClick={openModal}
            />
          )}
          <p className="-mb-3">Image</p>
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="file"
                id="composer-image-file"
                name="image"
                onChange={handleComposerFileChange}
                accept="image/*"
                className="pl-10 pr-3 py-2 bg-[#333] border border-[#555] rounded text-white"
              />
              <label
                htmlFor="composer-image-file"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
              >
                <img
                  src="/images/attach.svg"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleOpenPicker("image")}
              className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
            >
              <img
                src="/images/drive.png"
                alt="Google Drive"
                className="w-4 h-4"
              />
              Google Drive
            </button>
          </div>
          {(editingComposer?.coverImage || newComposer.coverImage) && (
            <ImagePreview
              image={editingComposer?.coverImage || newComposer.coverImage}
              onClick={openModal}
            />
          )}
          <p className="-mb-3">Cover Image</p>
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="file"
                id="composer-coverImage-file"
                name="coverImage"
                onChange={handleComposerFileChange}
                accept="image/*"
                className="pl-10 pr-3 py-2 bg-[#333] border border-[#555] rounded text-white"
              />
              <label
                htmlFor="composer-coverImage-file"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
              >
                <img
                  src="/images/attach.svg"
                  alt="Choose File"
                  className="w-4 h-4 filter invert"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => handleOpenPicker("image")}
              className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
            >
              <img
                src="/images/drive.png"
                alt="Google Drive"
                className="w-4 h-4"
              />
              Google Drive
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded transition-colors cursor-pointer"
            >
              {editingComposer ? "Update Composer" : "Add Composer"}
            </button>
            {editingComposer && (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 bg-[#666] hover:bg-[#555] text-white rounded transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    deleteComposer(editingComposer._id, editingComposer.hidden)
                  }
                  className="px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded transition-colors cursor-pointer"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </form>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search composers..."
          className="p-2 bg-[#333] border border-[#555] rounded text-white text-base w-full"
        />
      </div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
          Visible Composers
        </h3>
        <div className="flex justify-center md:justify-start mt-4">
          <label className="flex items-center gap-2 text-white cursor-pointer">
            <input
              type="checkbox"
              checked={showHiddenComposers}
              onChange={(e) => setShowHiddenComposers(e.target.checked)}
              className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
            />
            Show Hidden Composers
          </label>
        </div>
      </div>
      <div>
        <ul className="list-none p-0">
          {sortedVisibleComposers.length === 0 ? (
            <p className="text-[#ccc] text-center">
              {searchTerm
                ? "No visible composers match your search."
                : "No visible composers available."}
            </p>
          ) : (
            sortedVisibleComposers.map((composer) => (
              <li
                key={composer._id}
                className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]"
              >
                <span className="text-white flex-1 text-center md:text-left">
                  {composer.name}
                </span>
                <div className="flex flex-row gap-2 mt-2 md:mt-0">
                  <button
                    onClick={() => editComposer(composer)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors cursor-pointer"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => hideComposer(composer._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors cursor-pointer"
                  >
                    Hide
                  </button>
                  {composer.published ? (
                    <button
                      onClick={() => unpublishComposer(composer._id)}
                      className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                    >
                      Unpublish
                    </button>
                  ) : (
                    <button
                      onClick={() => publishComposer(composer._id)}
                      className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                    >
                      Publish
                    </button>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {showHiddenComposers && (
        <>
          <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-4">
            Hidden Composers
          </h3>
          <div className="max-h-[300px] overflow-y-auto">
            {sortedHiddenComposers.length === 0 ? (
              <p className="text-[#ccc] text-center">
                {searchTerm
                  ? "No hidden composers match your search."
                  : "No hidden composers found."}
              </p>
            ) : (
              <ul className="list-none p-0">
                {sortedHiddenComposers.map((composer) => (
                  <li
                    key={composer._id}
                    className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]"
                  >
                    <span className="text-white flex-1 text-center md:text-left">
                      {composer.name}
                    </span>
                    <div className="flex flex-row gap-2 mt-2 md:mt-0">
                      <button
                        onClick={() => editComposer(composer)}
                        className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors cursor-pointer"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => unhideComposer(composer._id)}
                        className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors cursor-pointer"
                      >
                        Unhide
                      </button>
                      {composer.published ? (
                        <button
                          onClick={() => unpublishComposer(composer._id)}
                          className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                        >
                          Unpublish
                        </button>
                      ) : (
                        <button
                          onClick={() => publishComposer(composer._id)}
                          className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded transition-colors cursor-pointer"
                        >
                          Publish
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ComposerTab;
